import { API_BASE, API_SCOPE, msalInstance } from "./authConfig";
import type {
  ApprovedUser, AttendanceMatrix, Coverage, DashboardSummary, DailyRecord, EmployeeDetail,
  EmployeeProfile, EmployeesSummary, ExecutiveOverview, Filters, HeatmapData, Location, LocationIntel,
  NotificationConfigT, Paginated, PolicyAssignment, PolicyConfigT, PolicyException, PolicyGroup,
  Rankings, RunResult, UploadBatchRow, UploadStatus,
} from "./types";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function getToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) throw new ApiError(401, "no_account", "Not signed in.");
  const result = await msalInstance.acquireTokenSilent({ scopes: [API_SCOPE], account });
  return result.accessToken;
}

function qs(params: object): string {
  const p = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    let code = "error";
    let message = res.statusText;
    try {
      const body = await res.json();
      code = body?.error?.code ?? code;
      message = body?.error?.message ?? message;
    } catch {
      /* non-JSON error */
    }
    throw new ApiError(res.status, code, message);
  }
  return res.json() as Promise<T>;
}

export const api = {
  locations: () => request<{ locations: Location[] }>("/locations"),
  dashboard: (f: Filters) => request<DashboardSummary>(`/dashboard/summary${qs(f)}`),
  attendance: (f: Filters, page = 1, pageSize = 50) =>
    request<Paginated<DailyRecord>>(`/attendance${qs({ ...f, page, page_size: pageSize })}`),
  employee: (id: string, f: Filters) =>
    request<EmployeeDetail>(`/employees/${encodeURIComponent(id)}${qs(f)}`),
  employeesSummary: (f: Filters, sort = "present_days", order = "desc", page = 1, pageSize = 25) =>
    request<EmployeesSummary>(`/employees${qs({ ...f, sort, order, page, page_size: pageSize })}`),
  attendanceMatrix: (f: Filters) => request<AttendanceMatrix>(`/attendance/matrix${qs(f)}`),
  heatmap: (f: Filters, limit = 20) =>
    request<HeatmapData>(`/attendance/heatmap${qs({ ...f, limit })}`),
  async exportMatrix(f: Filters, format: "csv" | "xlsx"): Promise<Blob> {
    const token = await getToken();
    const res = await fetch(`${API_BASE}/attendance/matrix${qs({ ...f, format })}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new ApiError(res.status, "export_failed", "Export failed.");
    return res.blob();
  },
  rankings: (f: Filters) => request<Rankings>(`/rankings${qs(f)}`),
  rankingsFull: (f: Filters, sort = "present_days", order = "desc") =>
    request<Rankings>(`/rankings${qs({ ...f, full: "true", sort, order })}`),
  overview: (f: Filters) => request<ExecutiveOverview>(`/analytics/overview${qs(f)}`),
  locationIntel: (f: Filters) => request<LocationIntel>(`/analytics/locations${qs(f)}`),
  coverage: (f: Filters) => request<Coverage>(`/analytics/coverage${qs(f)}`),
  profile: (id: string, f: Filters & { month?: string }) =>
    request<EmployeeProfile>(`/employees/${encodeURIComponent(id)}/profile${qs(f)}`),
  uploadStatus: (id: string, location: string) =>
    request<UploadStatus>(`/uploads/${encodeURIComponent(id)}${qs({ location })}`),
  initiateUpload: (filename: string, location: string) =>
    request<{ upload_id: string; upload_url: string | null; blob_path: string }>(
      "/uploads/initiate", { method: "POST", body: JSON.stringify({ filename, location }) }),
  // Direct upload through the API (no browser->Blob CORS); processes synchronously.
  async uploadFile(location: string, filename: string, file: File): Promise<UploadStatus> {
    const token = await getToken();
    const q = new URLSearchParams({ location, filename }).toString();
    const res = await fetch(`${API_BASE}/uploads?${q}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/octet-stream" },
      body: file,
    });
    if (!res.ok) {
      let code = "upload_failed", message = res.statusText;
      try { const b = await res.json(); code = b?.error?.code ?? code; message = b?.error?.message ?? message; }
      catch { /* non-JSON */ }
      throw new ApiError(res.status, code, message);
    }
    return res.json() as Promise<UploadStatus>;
  },
  adminUsers: () => request<{ users: ApprovedUser[] }>("/manage/users"),
  addUser: (object_id: string, role: string, display_name: string) =>
    request<ApprovedUser>("/manage/users",
      { method: "POST", body: JSON.stringify({ object_id, role, display_name }) }),
  removeUser: (id: string) =>
    request<{ removed: string }>(`/manage/users/${encodeURIComponent(id)}`, { method: "DELETE" }),
  addLocation: (code: string, name: string, timezone: string) =>
    request<Location>("/manage/locations",
      { method: "POST", body: JSON.stringify({ code, name, timezone }) }),
  removeLocation: (code: string) =>
    request<{ removed: string }>(`/manage/locations/${encodeURIComponent(code)}`, { method: "DELETE" }),
  adminUploads: (location?: string) =>
    request<{ uploads: UploadBatchRow[] }>(`/manage/uploads${qs({ location })}`),
  deleteUpload: (id: string, location: string) =>
    request<{ upload_id: string; daily_deleted: number; events_deleted: number }>(
      `/manage/uploads/${encodeURIComponent(id)}${qs({ location })}`, { method: "DELETE" }),
  deleteEmployee: (id: string, location: string) =>
    request<{ employee_id: string; daily_deleted: number; events_deleted: number; roster_removed: boolean }>(
      `/manage/employees/${encodeURIComponent(id)}${qs({ location })}`, { method: "DELETE" }),

  // ---- Attendance policy (admin) ----
  policyGroups: () => request<{ groups: PolicyGroup[] }>("/manage/policy/groups"),
  savePolicyGroup: (g: Partial<PolicyGroup>) =>
    request<PolicyGroup>("/manage/policy/groups", { method: "POST", body: JSON.stringify(g) }),
  deletePolicyGroup: (code: string) =>
    request<{ removed: string }>(`/manage/policy/groups/${encodeURIComponent(code)}`, { method: "DELETE" }),
  policyAssignments: () => request<{ assignments: PolicyAssignment[] }>("/manage/policy/assignments"),
  addPolicyAssignment: (a: Partial<PolicyAssignment>) =>
    request<PolicyAssignment>("/manage/policy/assignments", { method: "POST", body: JSON.stringify(a) }),
  assignBulk: (employee_ids: string[], group_code: string, location = "") =>
    request<{ assigned: number; group_code: string }>("/manage/policy/assign-bulk",
      { method: "POST", body: JSON.stringify({ employee_ids, group_code, location }) }),
  // One call for a CSV import: each row carries its own group/location/name.
  assignBulkItems: (items: { employee_id: string; group_code: string; location: string; employee_name: string }[]) =>
    request<{ assigned: number; skipped: number }>("/manage/policy/assign-bulk",
      { method: "POST", body: JSON.stringify({ items }) }),
  deletePolicyAssignment: (emp: string, addedAt: string) =>
    request<{ removed: string }>(
      `/manage/policy/assignments/${encodeURIComponent(emp)}${qs({ added_at: addedAt })}`, { method: "DELETE" }),
  policyConfig: () => request<PolicyConfigT>("/manage/policy/config"),
  savePolicyConfig: (c: Partial<PolicyConfigT>) =>
    request<PolicyConfigT>("/manage/policy/config", { method: "PUT", body: JSON.stringify(c) }),
  policyNotifications: () => request<NotificationConfigT>("/manage/policy/notifications"),
  savePolicyNotifications: (n: Partial<NotificationConfigT>) =>
    request<NotificationConfigT>("/manage/policy/notifications", { method: "PUT", body: JSON.stringify(n) }),
  policyRun: (body: Record<string, unknown>) =>
    request<RunResult>("/manage/policy/run", { method: "POST", body: JSON.stringify(body) }),
  policyTestEmail: (recipient: string) =>
    request<{ status: string; error?: string }>("/manage/policy/test-email",
      { method: "POST", body: JSON.stringify({ recipient }) }),
  policyExceptions: (type?: string, period?: string) =>
    request<{ exceptions: PolicyException[] }>(`/manage/policy/exceptions${qs({ type, period })}`),

  // Export returns a file; handled separately to stream the blob.
  async exportReport(f: Filters, format: "csv" | "xlsx"): Promise<Blob> {
    const token = await getToken();
    const res = await fetch(`${API_BASE}/reports/export${qs({ ...f, format })}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new ApiError(res.status, "export_failed", "Export failed.");
    return res.blob();
  },
};
